/**
 * HLOS-323: TS-side bridge para análise de imagem via Apple Vision.
 *
 * O Swift `AppleVisionRuntime` (chamado via `AppleLLMRuntimeBridge`
 * com método "visionAnalyze") extrai:
 * - OCR (VNRecognizeTextRequest)
 * - Tags/classificação (VNClassifyImageRequest)
 * - Cores dominantes
 *
 * Roda 100% on-device. Sem chamada de rede. Constitucional: CL-08.
 *
 * Este módulo é iOS-only. Web/Android rejeitam.
 */
import { Capacitor } from "@capacitor/core";
import { installHlosRouterOnce, registerVisionHandler } from "./hlosRouter";

export const DEFAULT_VISION_TIMEOUT_MS = 8_000; // Vision pode demorar em imagens grandes

export interface VisionAnalysis {
  ocrText: string;
  tags: string[];
  dominantColors: string[];
  imageWidth: number;
  imageHeight: number;
  isFallback: boolean;
}

interface PyodideLike {
  globals: {
    get(name: string): unknown;
    set(name: string, value: unknown): void;
  };
}

export class VisionBridge {
  private installed = false;

  /** iOS-only. Em web/Android, retorna análise vazia. */
  install(): boolean {
    if (this.installed) return true;
    if (!this.isIOS()) return false;
    installHlosRouterOnce();
    registerVisionHandler((raw) => {
      // raw tem forma { id, ok, result, error? }
      const r = raw as { ok?: boolean; result?: VisionAnalysis; error?: string };
      if (!r.ok || !r.result) {
        this.pendingResolvers.forEach((resolve) => resolve(EMPTY_ANALYSIS));
      } else {
        this.pendingResolvers.forEach((resolve) => resolve(r.result!));
      }
      this.pendingResolvers = [];
    });
    this.installed = true;
    return true;
  }

  isAvailable(): boolean {
    return this.isIOS() && this.installed;
  }

  private isIOS(): boolean {
    return Capacitor.getPlatform() === "ios";
  }

  private pendingResolvers: Array<(a: VisionAnalysis) => void> = [];

  /**
   * HLOS-323: instala `vision_bridge` no globalThis (e pyodide.globals)
   * para que o Python kernel possa chamá-lo via `import js; vision = js.vision_bridge`.
   * Idempotente.
   */
  injectIntoPyodide(pyodide: PyodideLike): boolean {
    if (!this.isAvailable()) {
      console.info("[HLOS][vision_bridge] NOT available (iOS only)");
      return false;
    }
    if (pyodide.globals.get("vision_bridge")) {
      console.info("[HLOS][vision_bridge] already injected");
      return true;
    }
    const w = globalThis as unknown as Record<string, unknown>;
    if (!w.vision_bridge) {
      w.vision_bridge = this;
    }
    try {
      pyodide.globals.set("vision_bridge", this);
    } catch {
      // ignore — globalThis já tem
    }
    console.info("[HLOS][vision_bridge] INJECTED into Pyodide");
    return true;
  }

  /**
   * HLOS-323: analisa uma imagem (data URL) via Apple Vision.
   * Retorna Promise com OCR + tags + cores.
   */
  async analyze(
    dataURL: string,
    lang: string = "pt-BR",
    timeoutMs: number = DEFAULT_VISION_TIMEOUT_MS,
  ): Promise<VisionAnalysis> {
    if (!this.isAvailable()) {
      throw new Error("Vision not available (iOS only or not installed)");
    }
    const id = `vis_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    return new Promise<VisionAnalysis>((resolve, reject) => {
      const timer = setTimeout(() => {
        const i = this.pendingResolvers.indexOf(resolve);
        if (i >= 0) this.pendingResolvers.splice(i, 1);
        reject(new Error(`Vision timeout after ${timeoutMs}ms`));
      }, timeoutMs);

      this.pendingResolvers.push((a: VisionAnalysis) => {
        clearTimeout(timer);
        resolve(a);
      });

      // Posta mensagem para Swift via window.webkit.messageHandlers.hlos
      const message = {
        id,
        method: "visionAnalyze",
        dataURL,
        lang,
      };
      const handler = (window as any).webkit?.messageHandlers?.hlos;
      if (!handler) {
        clearTimeout(timer);
        reject(new Error("webkit.messageHandlers.hlos not available"));
        return;
      }
      try {
        handler.postMessage(message);
      } catch (e) {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }
}

const EMPTY_ANALYSIS: VisionAnalysis = {
  ocrText: "",
  tags: [],
  dominantColors: [],
  imageWidth: 0,
  imageHeight: 0,
  isFallback: true,
};

export const visionBridge = new VisionBridge();
